import React, { useState } from "react";

import {
  AcceptanceButton,
  AcceptanceFooter,
  AcceptanceTextField,
} from "layouts/AcceptancePage/components";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import clsx from "clsx";
import useMutationOnboardInstructorLogin from "./hooks/useMutationOnboardInstructorLogin";

type Props = {
  brand: string;
  personKey: string;
  agreementKey: string;
};

const useStyles = makeStyles(theme => ({
  header: {
    fontSize: "20px",
    fontWeight: 700,
    "&.bsm": {
      fontWeight: 500,
    },
    lineHeight: "24px",
    letterSpacing: "0.3px",
  },
  text: {
    fontSize: "18px",
    fontWeight: 400,
    "&.bsm": {
      fontWeight: 300,
    },
    lineHeight: "27px",
  },
  inputField: {
    [theme.breakpoints.up("sm")]: {
      width: "302px",
    },
  },
}));

const AcceptancePageCheck: React.FC<Props> = ({
  brand,
  personKey,
  agreementKey,
}) => {
  const [email, setEmail] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const [postcode, setPostcode] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const history = useHistory();
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);

  const onError = () => {
    setIsLoading(false);
    enqueueSnackbar(
      "We're unable to verify you.  Please get in touch with our team so we can review your details",
      {
        variant: "error",
        autoHideDuration: 2000,
      },
    );
  };

  const [searchAgreementKey] = useMutationOnboardInstructorLogin({
    onCompleted: async data => {
      setIsLoading(false);
      if (!data.onboardInstructorLogin?.accessToken) {
        onError();
        return;
      }
      localStorage.setItem(
        "instructorAuth",
        data.onboardInstructorLogin?.accessToken,
      );
      enqueueSnackbar("Success!", {
        variant: "success",
        autoHideDuration: 2000,
      });
      history.push(`/acceptance-page/${agreementKey}`);
    },
    onError,
  });

  const clickButton = () => {
    setIsLoading(true);
    searchAgreementKey({
      variables: {
        input: {
          email,
          postcode,
          personKey,
          dateOfBirth,
          brand,
        },
      },
    });
  };

  return (
    <form>
      <Box marginBottom={3}>
        <Typography
          component="h1"
          className={clsx(classes.header, {
            bsm: brand === "BSM",
          })}
        >
          Verify it&apos;s you
        </Typography>
      </Box>
      <Box marginBottom={3}>
        <Typography
          color="textPrimary"
          variant="body1"
          className={clsx(classes.text, {
            bsm: brand === "BSM",
          })}
        >
          Before you continue, please confirm a few personal details attached to
          your account below.
        </Typography>
      </Box>

      <Box marginTop={3}>
        <AcceptanceTextField
          id="postcode"
          className={classes.inputField}
          fullWidth
          label="Postcode"
          onChange={e => {
            e.stopPropagation();
            setPostcode(e.target.value);
          }}
        />
      </Box>
      <Box marginTop={4}>
        <AcceptanceTextField
          onChange={e => {
            e.stopPropagation();
            setDateOfBirth(e.target.value);
          }}
          id="dateOfBirth"
          className={classes.inputField}
          fullWidth
          type="date"
          label="Date of Birth"
        />
      </Box>
      <Box marginTop={4}>
        <AcceptanceTextField
          onChange={e => {
            e.stopPropagation();
            setEmail(e.target.value);
          }}
          id="email"
          className={classes.inputField}
          fullWidth
          type="email"
          label="Email"
        />
      </Box>
      <Box marginTop={3}>
        <AcceptanceButton
          brand={brand}
          disabled={isLoading}
          onClick={() => {
            clickButton();
          }}
        >
          Verify
        </AcceptanceButton>
      </Box>
      <AcceptanceFooter brand={brand} />
    </form>
  );
};

export default AcceptancePageCheck;
